import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import provideContext from "lib/provideContext";
import { getAuthToken, default as railsFetch } from "lib/railsFetch";
import wrapProvider from "lib/wrapProvider";
import { store } from "reducers";
import DOMPurify from 'dompurify';
import { Button, Icon, Modal } from "@devforce/tds-react";
import setModalAppElement from 'lib/setModalAppElement';
import { getLocale, localizeDate } from "@devforce/trailhead-client-locale";
import * as env from "@devforce/trailhead-client-env";
import { buildScopeTranslate } from "lib/I18n";
import {
  initSpecialityOrgManager,
  connectOrg,
  provisionOrg,
  initRequestOrg,
  requestOrg,
  setEmail,
  showError,
} from "actions/specialityOrgActions";
import { setSelectedOrg as setSelectedOrgAction } from "actions/orgPicker";
import Poller from "lib/Poller";
import { specialityOrgStatus } from "reducers/specialityOrg";
import LoginModalBtn from "components/auth/buttons/LoginModalBtn";
import IconContext from "components/utils/IconContext";
import track from "lib/tracking/track";
import { TH_ORG_PICKER_REFRESH_EVENT } from 'lib/orgPickerEvents';

const POLLING_INTERVAL = 5000;
const POLLING_MAX_ATTEMPTS = 1200;
const ERROR_RETRY_COUNT = 1;
const tManage = buildScopeTranslate("speciality_org.manage");
const missingEmailHelpLink = "https://trailhead.salesforce.com/help?article=Trailhead-Account-Email";
const genericHelpLink = "https://trailhead.salesforce.com/help?article=Connect-a-Salesforce-or-Social-Login-to-Your-Trailhead-Account";

// Module-level variable to track active poller across all component instances
// This prevents multiple instances from creating duplicate pollers
let activePoller = null;

setModalAppElement();

const timeFormatter = new Intl.DateTimeFormat(getLocale(), {
  hour: "numeric",
  minute: "numeric",
  timeZoneName: "short",
  hour12: true,
});

function setSelectedOrgForConnectedPlayground(setSelectedOrg, orgName) {
  if (setSelectedOrg) {
    setSelectedOrg({
      name: orgName,
      active_org: true,
      template_match: true,
    });
  }
}

async function setActiveOrgAndRefreshOrgPicker(orgId) {
  if (orgId) {
    await railsFetch({
      url: '/user_orgs/active',
      method: 'post',
      data: { id: orgId },
    }).catch(() => {});
  }
  document.dispatchEvent(new CustomEvent(TH_ORG_PICKER_REFRESH_EVENT));
}

export function SpecialityOrgManager({
  authenticated,
  authorizedAccount,
  connectOrg,
  email,
  initRequestOrg,
  orgData,
  orgLeaseTerm,
  orgRequestPending,
  previewMode,
  productLabel,
  provisionOrg,
  requestOrg,
  setEmail,
  setSelectedOrg,
  showError,
  status,
  statusUrl,
  tbidEmail,
  testId,
  unitUid,
  moduleApiName,
  unitApiName,
}) {
  const hasOrgProvisioned = !!orgData;
  const isInitialState = [specialityOrgStatus.INITIAL, specialityOrgStatus.ERROR].includes(status);
  const pollerRef = useRef(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  /*global newrelic*/
  const reportError = (error) => {
    if (typeof newrelic != "undefined") {
      newrelic.noticeError(error);
    }
  }

  const createPoller = (statusUrl, errorRetryCount = 0) => {
    return new Poller({
      createRequest: () => railsFetch({ url: statusUrl, method: "get" }),
      interval: POLLING_INTERVAL,
      isComplete: (response) => {
        return response.data.state !== "pending";
      },
      maxRetries: POLLING_MAX_ATTEMPTS,
      onComplete: async (response) => {
        // Clear the module-level activePoller reference
        if (activePoller) {
          activePoller = null;
        }
        if (response.data.state === "created") {
          if (response.data.auto_login) {
            try {
              const connectResponse = await railsFetch({
                url: `${statusUrl}/playground_connection`,
                method: "post"
              });
              connectOrg({
                productName: connectResponse.data.name,
                expiresAt: connectResponse.data.expires_at,
                expiresSoon: connectResponse.data.expires_soon,
                launchPath: connectResponse.data.launch_de_path,
                templateUri: connectResponse.data.template_uri,
                orgType: connectResponse.data.type,
                provider: connectResponse.data.provider,
              });
              setSelectedOrgForConnectedPlayground(setSelectedOrg, connectResponse.data.name);
              if (previewMode) {
                await setActiveOrgAndRefreshOrgPicker(connectResponse.data.id);
              }
            } catch (error) {
              if (error.railsFetchStatusCode === 422) {
                showError({statusUrl});
                return;
              } else if ((error.railsFetchStatusCode >= 500 || 'railsFetchError' in error) && errorRetryCount < ERROR_RETRY_COUNT) {
                errorRetryCount++;
              } else {
                newrelic.noticeError(error);
                showError(error);
                return;
              }
            }
          } else {
            provisionOrg({
              orgId: response.data.organization_id,
              expiresAt: response.data.expires_at,
              productName: response.data.name || productLabel,
              username: response.data.username,
              orgRequestId: response.data.id,
            });
          }
        } else if (response.data.state === "failed") {
          showError({statusUrl});
        }
        return;
      },
      onError: (error, poller) => {
        if ([401, 403, 404].includes(error.railsFetchStatusCode)) {
          window.location.reload();
        }
        else if ((error.railsFetchStatusCode >= 500 || 'railsFetchError' in error) && errorRetryCount < ERROR_RETRY_COUNT) {
          errorRetryCount++;
          poller.retry();
        }
        else {
          // Clear the module-level activePoller reference on terminal errors
          if (activePoller) {
            activePoller = null;
          }
          reportError(new Error(JSON.stringify(error)));
          showError(error);
        }
      },
      onTimeout: () => {
        // Clear the module-level activePoller reference on timeout
        if (activePoller) {
          activePoller = null;
        }
        showError({});
      },
    });
  };

  const deletePoller = (statusUrl) => {
    return new Promise((resolve, reject) => {
      const poller = new Poller({
        createRequest: () => railsFetch({ url: statusUrl, method: "delete" }),
        interval: POLLING_INTERVAL,
        maxRetries: ERROR_RETRY_COUNT,
        isComplete: (response) => {
          return response && response.ok;
        },
        onComplete: (response) => {
          resolve();
        },
        onError: (error, poller) => {
          if (error.railsFetchStatusCode === 404) {
            poller.stop();
            resolve();
          } else if (error.railsFetchStatusCode >= 500 || "railsFetchError" in error) {
            poller.retry();
          } else {
            poller.stop();
            reject(error);
          }
        },
        onTimeout: () => {
          reject({
            message: "Timeout while deleting org request",
            railsFetchStatusCode: 500,
          });
        },
      });
      poller.start();
    });
  };

  // Set the initial state if the user has an existing org request
  useEffect(() => {
    if (tbidEmail) {
      setEmail({ email: tbidEmail });
    }
    if (authorizedAccount) {
      if (authorizedAccount.org_request) {
        const orgRequest = authorizedAccount.org_request;
        if (orgRequest.state === "pending") {
          // Only create a poller if one is not already active across all instances
          // This prevents multiple component instances from creating duplicate pollers
          if (!activePoller) {
            requestOrg({
              statusUrl: orgRequest.status_url,
              email: orgRequest.email,
            });
            // Stop any existing local poller before creating a new one
            pollerRef.current?.stop();
            const pollerInstance = createPoller(orgRequest.status_url);
            pollerRef.current = pollerInstance;
            activePoller = pollerInstance;
            pollerInstance.start();
          }
        } else if (orgRequest.state === "created") {
          if (orgRequest.auto_login) {
            railsFetch({
              url: `${orgRequest.status_url}/playground_connection`,
              method: "post",
            }).then(async (connectResponse) => {
              connectOrg({
                productName: connectResponse.data.name,
                expiresAt: connectResponse.data.expires_at,
                expiresSoon: connectResponse.data.expires_soon,
                launchPath: connectResponse.data.launch_de_path,
                templateUri: connectResponse.data.template_uri,
                orgType: connectResponse.data.type,
                provider: connectResponse.data.provider,
              });
              setSelectedOrgForConnectedPlayground(setSelectedOrg, connectResponse.data.name);
              if (previewMode) {
                await setActiveOrgAndRefreshOrgPicker(connectResponse.data.id);
              }
            }).catch((error) => {
              if (error.railsFetchStatusCode === 422) {
                showError({ statusUrl: orgRequest.status_url });
              } else {
                newrelic.noticeError(error);
                showError(error);
              }
            });
          } else {
            provisionOrg({
              orgId: orgRequest.organization_id,
              expiresAt: authorizedAccount.expires_at,
              expiresSoon: authorizedAccount.expires_soon,
              launchPath: authorizedAccount.launch_de_path,
              productName: authorizedAccount.name,
              username: orgRequest.username,
              orgRequestId: orgRequest.id,
            });
          }
        } else if (orgRequest.state === "failed") {
          showError({ statusUrl: orgRequest.status_url });
        }
      }
      if (authorizedAccount.active_org) {
        connectOrg({
          productName: authorizedAccount.name,
          expiresAt: authorizedAccount.expires_at,
          expiresSoon: authorizedAccount.expires_soon,
          launchPath: authorizedAccount.launch_de_path,
          templateUri: authorizedAccount.template_uri,
          orgType: authorizedAccount.type,
          provider: authorizedAccount.provider,
        });
      }
    }
    return () => {
      pollerRef.current?.stop();
      if (pollerRef.current === activePoller) {
        activePoller = null;
      }
    };
  }, []);

  const createOrgRequest = () => {
    // Make a POST request to api/v1/orgs/provision_requests with the unit uid
    railsFetch({
      url: `/api/v1/orgs/provision_requests`,
      method: "post",
      data: {
        unit_uid: unitUid,
      }
    })
      .then((response) => {
        const statusUrl = `/api/v1/orgs/provision_requests/${response.data.id}`;
        if (response.data.state === "pending") {
          requestOrg({ statusUrl, email: response.data.email });
          // Stop any existing poller before creating a new one
          if (activePoller) {
            activePoller.stop();
            activePoller = null;
          }
          pollerRef.current?.stop();
          const pollerInstance = createPoller(statusUrl);
          pollerRef.current = pollerInstance;
          activePoller = pollerInstance;
          pollerInstance.start();
        } else if (response.data.state === "created") {
          provisionOrg({
            orgId: response.data.organization_id,
            expiresAt: response.data.expires_at,
            productName: response.data.name || authorizedAccount?.name || productLabel,
            username: response.data.username,
            orgRequestId: response.data.id,
          });
        } else {
          showError({ statusUrl });
        }
      })
      .catch((error) => {
        reportError(new Error(JSON.stringify(error)));
        // If the user encounters a 409 Conflict (likely due to already having requested a playground in a separate tab), refresh the page.
        if (error.railsFetchStatusCode === 409) {
          return window.location.reload();
        }
        showError(error);
      });
  };

  const launchButtonClickedEvent = {
    eventName: 'tpo_launch_clicked',
    payload: {
      pageLocation: 'speciality_org_manager',
      moduleApiName: moduleApiName,
      unitApiName: unitApiName,
    },
  };

  const handleSubmitButtonAction = async (e) => {
    e.preventDefault();
    if (hasOrgProvisioned) {
      if (status === specialityOrgStatus.CONNECTED && orgData.launchPath) {
        track(launchButtonClickedEvent.eventName, {
          ...launchButtonClickedEvent.payload,
          playgroundInfo: {
            playgroundType: orgData.orgType,
            template: {
              name: orgData.templateUri,
              provider: orgData.provider,
            }
          }
        });
        return window.open(orgData.launchPath, "_blank");
      } else {
        window.location.href = `/sessions/de/new?org_request_id=${orgData.orgRequestId}`;
        return;
      }
    }
    // Show confirmation modal for new playground creation
    setShowConfirmationModal(true);
  };

  const handleCreatePlayground = async () => {
    // Close the modal first
    setShowConfirmationModal(false);
    
    // Set loading state while waiting for network request to resolve
    initRequestOrg();
    // If an org request is in an error state, delete the previous org request first.
    if (status === specialityOrgStatus.ERROR && statusUrl) {
      if (activePoller) {
        activePoller.stop();
        activePoller = null;
      }
      pollerRef.current?.stop();
      try {
        await deletePoller(statusUrl);
      } catch (error) {
        reportError(new Error(JSON.stringify(error)));
        showError(error);
        return;
      }
    }
    createOrgRequest();
  };

  const title = hasOrgProvisioned
    ? tManage(status === specialityOrgStatus.CONNECTED ? "title.connected" : "title.provisioned", {
        type: productLabel || ""
      })
    : tManage(orgRequestPending ? "title.requesting" : "title.intro", { type: productLabel || "" });
  const buttonLabel = hasOrgProvisioned
    ? status === specialityOrgStatus.CONNECTED
      ? tManage("button.launch")
      : tManage("button.connect_org")
    : tManage("button.request_org");
  
    const confirmButtonLabel = tManage("button.confirm_org");
    const dismissButtonLabel = tManage("button.dismiss_org");
    const modalHeadingLabel = tManage("modal.heading");

  const renderPreviewMode = () => {
    return (
      <span className="slds-badge th-badge--error">
        <Icon variant="netural" size="x-small" name="priority"/>
        Preview Mode
      </span>
    );
  };

  const renderExpirationDate = () => {
    const hasExpiration = status === specialityOrgStatus.CONNECTED && orgData?.expiresAt;
    const expirationBadge = hasExpiration && (() => {
      const { expiresAt, expiresSoon } = orgData;
      const expiresAtDate = new Date(expiresAt);
      const localizedDate = localizeDate(expiresAt);
      // Remove the zero-width space character, since jest tests in sfci only use regular spaces,
      // causing the snapshot tests to fail.
      const localizedTime = timeFormatter.format(expiresAtDate).replace(/\u202F/g, " ");
      return (
        <div className={`slds-badge ${expiresSoon ? "th-badge--error" : "th-badge--warning"}`}>
          <Icon
            assistiveText={{
              label: ""
            }}
            variant="neutral"
            size="x-small"
            name={expiresSoon ? "priority" : "clock"}
          />
          {tManage(expiresSoon ? "expiration.expires_soon" : "expiration.expires", {
            date: localizedDate,
            time: localizedTime
          })}
        </div>
      );
    })();

    if (previewMode) {
      return (
        <div className="slds-grid slds-grid_vertical-align-center slds-gutter_x-small">
          {expirationBadge}
          {renderPreviewMode()}
        </div>
      );
    }
    return expirationBadge;
  };

  const renderDescription = () => {
    if (hasOrgProvisioned) {
      if (status === specialityOrgStatus.CONNECTED) {
        return (
          <p
            className="th-text--small"
            dangerouslySetInnerHTML={{
              __html: tManage("description.connected", { type: productLabel })
            }}
          ></p>
        );
      } else {
        return (
          <>
            <p className="th-text--small">{tManage("description.provisioned")}</p>
            <ul className="slds-list_dotted th-text--small">
              <li>{tManage("description.provisioned_step_1")}</li>
              <li
                dangerouslySetInnerHTML={{
                  __html: tManage("description.provisioned_step_2", { button_name: tManage("button.connect_org") })
                }}
              ></li>
              <li
                dangerouslySetInnerHTML={{
                  __html: tManage("description.provisioned_step_3", { username: orgData.username || '' })
                }}></li>
              <li>{tManage("description.provisioned_step_4")}</li>
              <li>{tManage("description.provisioned_password_reset_note")}</li>
            </ul>
          </>
        );
      }
    }
    return (
      <p
        className="th-text--small"
        dangerouslySetInnerHTML={{
          __html: tManage("description.intro", { type: productLabel })
        }}
      ></p>
    );
  };



  const renderStatusPartial = ({ type, icon, text }) => {
    return (
      <div className="th-speciality-org-manager__status">
        <div className={`th-speciality-org-manager__status--text th-speciality-org-manager__status--${type}`}>
          <Icon
            assistiveText={{
              label: icon
            }}
            variant="neutral"
            size="x-small"
            name={icon}
          />
          <div>
            {text.map((line, index) => (
              <div
                key={index}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(line, {ADD_ATTR: ['target'] })
                }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderStatus = () => {
    if (status === specialityOrgStatus.ERROR) {
      return renderStatusPartial({
        type: "error",
        icon: "error",
        text: [tManage("status.generic_error", { type: productLabel, link: genericHelpLink })],
      });
    } else if (status === specialityOrgStatus.PROVISIONED) {
      return renderStatusPartial({
        type: "success",
        icon: "notification",
        text: [tManage("status.provisioned", { email, link: missingEmailHelpLink })],
      });
    }
  };

  const renderConfirmationModal = () => {
    return (
      <Modal
        id="orgfarm-confirmation-modal"
        isOpen={showConfirmationModal}
        heading={modalHeadingLabel}
        onRequestClose={() => setShowConfirmationModal(false)}
        containerClassName="slds-container--large orgfarm-confirmation-modal__container"
        headerClassName="orgfarm-confirmation-modal__header"
        footer={[
          <Button
            key="not-yet"
            label={dismissButtonLabel}
            variant="neutral"
            onClick={() => setShowConfirmationModal(false)}
          />,
          <Button
            key="create-playground"
            label={confirmButtonLabel}
            variant="brand"
            onClick={handleCreatePlayground}
          />,
        ]}
        size="small"
      >
        <div className="slds-p-around_large">
          { orgLeaseTerm ?
            (<p className="slds-text-body_regular" dangerouslySetInnerHTML={{
                __html: tManage("modal.body", { checkout_days: orgLeaseTerm })
              }}></p>
            )
          :  
            (<p className="slds-text-body_regular" dangerouslySetInnerHTML={{
                __html: tManage("modal.body_default", { checkout_days: orgLeaseTerm })
              }}></p>
            )
          }
          <p className="slds-text-body_regular slds-m-top_medium" dangerouslySetInnerHTML={{
            __html: tManage("modal.description")
          }}></p>
        </div>
      </Modal>
    );
  };

  return (
    <div className="th-speciality-org-manager__container">
      <IconContext>
        <div className="th-speciality-org-manager__content">
          <div className="th-speciality-org-manager__left-container">
            <div className="th-speciality-org-manager__heading">
              {renderExpirationDate()}
              <h3 className="th-text--medium th-text--bold tds-color_midnight">{title}</h3>
            </div>
            {renderDescription()}
          </div>
          <div className="th-speciality-org-manager__right-container">
            {authenticated ? (
              <Button
                spinner={orgRequestPending}
                id="org-request-button"
                disabled={orgRequestPending}
                label={buttonLabel}
                variant={status === specialityOrgStatus.CONNECTED ? "brand" : "neutral"}
                onClick={handleSubmitButtonAction}
              />
            ) : (
              <LoginModalBtn className={"slds-button slds-button_neutral"} content={tManage("button.login")} />
            )}
          </div>
        </div>
        {renderStatus()}
        {renderConfirmationModal()}
      </IconContext>
    </div>
  );
}

SpecialityOrgManager.propTypes = {
  authenticated: PropTypes.bool.isRequired,
  authorizedAccount: PropTypes.object,
  email: PropTypes.string,
  orgData: PropTypes.object,
  orgLeaseTerm: PropTypes.number,
  orgRequestPending: PropTypes.bool,
  previewMode: PropTypes.bool,
  productLabel: PropTypes.string,
  setSelectedOrg: PropTypes.func,
  status: PropTypes.string,
  statusUrl: PropTypes.string,
  tbidEmail: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  unitUid: PropTypes.string.isRequired,
  moduleApiName: PropTypes.string.isRequired,
  unitApiName: PropTypes.string.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  initRequestOrg: () => dispatch(initRequestOrg()),
  setEmail: (email) => dispatch(setEmail(email)),
  requestOrg: (unitUid) => dispatch(requestOrg(unitUid)),
  provisionOrg: (orgData) => dispatch(provisionOrg(orgData)),
  connectOrg: (orgData) => dispatch(connectOrg(orgData)),
  showError: (error) => dispatch(showError(error)),
  setSelectedOrg: (org) => dispatch(setSelectedOrgAction(org)),
});

export default provideContext(
  wrapProvider({
    store,
    initAction: initSpecialityOrgManager,
    mapStateToProps: (state) => ({
      email: state.specialityOrg.email,
      orgData: state.specialityOrg.orgData,
      orgRequestPending: state.specialityOrg.orgRequestPending,
      status: state.specialityOrg.status,
      statusUrl: state.specialityOrg.statusUrl,
    }),
    mapDispatchToProps,
  })(SpecialityOrgManager)
);
